/**
 * Property Map block — editor component.
 *
 * @package
 * @since   3.5.0
 */

import { __ } from '@wordpress/i18n';
import { useBlockProps, InspectorControls } from '@wordpress/block-editor';
import {
	PanelBody,
	RangeControl,
	SelectControl,
	ToggleControl,
	TextControl,
} from '@wordpress/components';

import BlockPreview from '../shared/BlockPreview';

const STYLE_OPTIONS = [
	{ label: __( 'Standard', 'havenlytics' ), value: 'standard' },
	{ label: __( 'Light', 'havenlytics' ), value: 'light' },
	{ label: __( 'Dark', 'havenlytics' ), value: 'dark' },
];

const MARKER_SIZE_OPTIONS = [
	{ label: __( 'Small', 'havenlytics' ), value: 'sm' },
	{ label: __( 'Medium', 'havenlytics' ), value: 'md' },
	{ label: __( 'Large', 'havenlytics' ), value: 'lg' },
];

const MARKER_STYLE_OPTIONS = [
	{ label: __( 'Pin', 'havenlytics' ), value: 'pin' },
	{ label: __( 'Dot', 'havenlytics' ), value: 'dot' },
];

const POPUP_STYLE_OPTIONS = [
	{ label: __( 'Default card', 'havenlytics' ), value: 'default' },
	{ label: __( 'Compact card', 'havenlytics' ), value: 'compact' },
];

const POPUP_TRIGGER_OPTIONS = [
	{ label: __( 'Open on click', 'havenlytics' ), value: 'click' },
	{ label: __( 'Open on hover', 'havenlytics' ), value: 'hover' },
];

export default function Edit( { attributes, setAttributes } ) {
	const {
		height,
		zoom,
		clustering,
		clusterRadius,
		clusterMaxZoom,
		mapStyle,
		currentLocation,
		autoFit,
		scrollWheel,
		postsPerPage,
		showAll,
		featuredOnly,
		department,
		status,
		propertyType,
		markerSize,
		markerStyle,
		popupStyle,
		popupWidth,
		popupTrigger,
		animations,
		showPrice,
		showFavorite,
		showStatus,
		showMeta,
		showCta,
	} = attributes;
	const blockProps = useBlockProps();

	return (
		<>
			<InspectorControls>
				<PanelBody
					title={ __( 'Map', 'havenlytics' ) }
					initialOpen={ true }
				>
					<p className="components-base-control__help">
						{ __(
							'Uses your global Havenlytics map provider and API settings.',
							'havenlytics'
						) }
					</p>
					<RangeControl
						label={ __( 'Height (px)', 'havenlytics' ) }
						value={ height }
						min={ 240 }
						max={ 1000 }
						step={ 20 }
						onChange={ ( value ) =>
							setAttributes( { height: value } )
						}
						__nextHasNoMarginBottom
					/>
					<RangeControl
						label={ __( 'Default zoom', 'havenlytics' ) }
						value={ zoom }
						min={ 1 }
						max={ 20 }
						onChange={ ( value ) =>
							setAttributes( { zoom: value } )
						}
						__nextHasNoMarginBottom
					/>
					<SelectControl
						label={ __( 'Map style', 'havenlytics' ) }
						value={ mapStyle }
						options={ STYLE_OPTIONS }
						onChange={ ( value ) =>
							setAttributes( { mapStyle: value } )
						}
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Auto-fit to markers', 'havenlytics' ) }
						checked={ !! autoFit }
						onChange={ ( value ) =>
							setAttributes( { autoFit: value } )
						}
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Marker clustering', 'havenlytics' ) }
						checked={ !! clustering }
						onChange={ ( value ) =>
							setAttributes( { clustering: value } )
						}
						__nextHasNoMarginBottom
					/>
					{ !! clustering && (
						<>
							<RangeControl
								label={ __( 'Cluster radius', 'havenlytics' ) }
								help={ __(
									'How close markers must be to group.',
									'havenlytics'
								) }
								value={ clusterRadius }
								min={ 20 }
								max={ 180 }
								step={ 4 }
								onChange={ ( value ) =>
									setAttributes( { clusterRadius: value } )
								}
								__nextHasNoMarginBottom
							/>
							<RangeControl
								label={ __(
									'Disable clustering at zoom',
									'havenlytics'
								) }
								help={ __(
									'0 keeps clustering at every zoom level.',
									'havenlytics'
								) }
								value={ clusterMaxZoom }
								min={ 0 }
								max={ 19 }
								onChange={ ( value ) =>
									setAttributes( { clusterMaxZoom: value } )
								}
								__nextHasNoMarginBottom
							/>
						</>
					) }
					<ToggleControl
						label={ __( 'Current location button', 'havenlytics' ) }
						checked={ !! currentLocation }
						onChange={ ( value ) =>
							setAttributes( { currentLocation: value } )
						}
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Scroll-wheel zoom', 'havenlytics' ) }
						checked={ !! scrollWheel }
						onChange={ ( value ) =>
							setAttributes( { scrollWheel: value } )
						}
						__nextHasNoMarginBottom
					/>
				</PanelBody>

				<PanelBody
					title={ __( 'Markers', 'havenlytics' ) }
					initialOpen={ false }
				>
					<SelectControl
						label={ __( 'Marker size', 'havenlytics' ) }
						value={ markerSize }
						options={ MARKER_SIZE_OPTIONS }
						onChange={ ( value ) =>
							setAttributes( { markerSize: value } )
						}
						__nextHasNoMarginBottom
					/>
					<SelectControl
						label={ __( 'Marker style', 'havenlytics' ) }
						value={ markerStyle }
						options={ MARKER_STYLE_OPTIONS }
						onChange={ ( value ) =>
							setAttributes( { markerStyle: value } )
						}
						__nextHasNoMarginBottom
					/>
				</PanelBody>

				<PanelBody
					title={ __( 'Popup card', 'havenlytics' ) }
					initialOpen={ false }
				>
					<SelectControl
						label={ __( 'Popup style', 'havenlytics' ) }
						value={ popupStyle }
						options={ POPUP_STYLE_OPTIONS }
						onChange={ ( value ) =>
							setAttributes( { popupStyle: value } )
						}
						__nextHasNoMarginBottom
					/>
					<RangeControl
						label={ __( 'Popup width (px)', 'havenlytics' ) }
						value={ popupWidth }
						min={ 240 }
						max={ 360 }
						step={ 10 }
						onChange={ ( value ) =>
							setAttributes( { popupWidth: value } )
						}
						__nextHasNoMarginBottom
					/>
					<SelectControl
						label={ __( 'Popup trigger', 'havenlytics' ) }
						help={ __(
							'Touch devices always open on tap.',
							'havenlytics'
						) }
						value={ popupTrigger }
						options={ POPUP_TRIGGER_OPTIONS }
						onChange={ ( value ) =>
							setAttributes( { popupTrigger: value } )
						}
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Animations', 'havenlytics' ) }
						help={ __(
							'Marker pulse and popup entrance. Reduced-motion preferences are always honored.',
							'havenlytics'
						) }
						checked={ !! animations }
						onChange={ ( value ) =>
							setAttributes( { animations: value } )
						}
						__nextHasNoMarginBottom
					/>
				</PanelBody>

				<PanelBody
					title={ __( 'Display', 'havenlytics' ) }
					initialOpen={ false }
				>
					<ToggleControl
						label={ __( 'Show price', 'havenlytics' ) }
						checked={ !! showPrice }
						onChange={ ( value ) =>
							setAttributes( { showPrice: value } )
						}
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show favorite button', 'havenlytics' ) }
						checked={ !! showFavorite }
						onChange={ ( value ) =>
							setAttributes( { showFavorite: value } )
						}
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show status badge', 'havenlytics' ) }
						checked={ !! showStatus }
						onChange={ ( value ) =>
							setAttributes( { showStatus: value } )
						}
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __(
							'Show meta (beds/baths/area)',
							'havenlytics'
						) }
						checked={ !! showMeta }
						onChange={ ( value ) =>
							setAttributes( { showMeta: value } )
						}
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show CTA button', 'havenlytics' ) }
						checked={ !! showCta }
						onChange={ ( value ) =>
							setAttributes( { showCta: value } )
						}
						__nextHasNoMarginBottom
					/>
				</PanelBody>

				<PanelBody
					title={ __( 'Properties', 'havenlytics' ) }
					initialOpen={ false }
				>
					<ToggleControl
						label={ __( 'Show all properties', 'havenlytics' ) }
						help={ __(
							'Loads every mappable property (capped at 500 for performance).',
							'havenlytics'
						) }
						checked={ !! showAll }
						onChange={ ( value ) =>
							setAttributes( { showAll: value } )
						}
						__nextHasNoMarginBottom
					/>
					{ ! showAll && (
						<RangeControl
							label={ __( 'Max properties', 'havenlytics' ) }
							value={ postsPerPage }
							min={ 1 }
							max={ 200 }
							step={ 1 }
							onChange={ ( value ) =>
								setAttributes( { postsPerPage: value } )
							}
							__nextHasNoMarginBottom
						/>
					) }
					<ToggleControl
						label={ __( 'Featured only', 'havenlytics' ) }
						checked={ !! featuredOnly }
						onChange={ ( value ) =>
							setAttributes( { featuredOnly: value } )
						}
						__nextHasNoMarginBottom
					/>
					<TextControl
						label={ __( 'Department (slug)', 'havenlytics' ) }
						value={ department }
						onChange={ ( value ) =>
							setAttributes( { department: value } )
						}
						__nextHasNoMarginBottom
					/>
					<TextControl
						label={ __( 'Status (slug)', 'havenlytics' ) }
						value={ status }
						onChange={ ( value ) =>
							setAttributes( { status: value } )
						}
						__nextHasNoMarginBottom
					/>
					<TextControl
						label={ __( 'Property type (slug)', 'havenlytics' ) }
						value={ propertyType }
						onChange={ ( value ) =>
							setAttributes( { propertyType: value } )
						}
						__nextHasNoMarginBottom
					/>
				</PanelBody>
			</InspectorControls>

			<div { ...blockProps }>
				<BlockPreview
					block="havenlytics/property-map"
					attributes={ attributes }
				/>
			</div>
		</>
	);
}
